import { Suspense, useEffect, useRef } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import MobileBottomNav from "./components/MobileBottomNav";
import TapFeedbackListener from "./components/TapFeedbackListener";
import FeedbackWidget from "./components/FeedbackWidget";
import { WishlistProvider } from "./contexts/WishlistContext";
import { VehicleProvider } from "./contexts/VehicleContext";
import {
  initVoomTracker,
  trackPageView,
  setTrackerUserId,
} from "./lib/voom-tracker";
import { lazyWithRetry, installStaleSpaRecovery } from "./lib/lazy-retry";
import { useAuth } from "./_core/hooks/useAuth";

// Register the global vite:preloadError listener once on module load.
// Handles stale-SPA chunk-load failures caused by post-deploy hash changes.
installStaleSpaRecovery();

// The Toaster (sonner) only needs to exist before the first toast fires —
// never on first paint. Lazy-load it out of the critical chunk. sonner's
// toast() queues calls, so any toast that fires before the Toaster mounts is
// flushed as soon as it does. Rendered in a low-priority <Suspense fallback={null}>.
const Toaster = lazyWithRetry(() =>
  import("@/components/ui/sonner").then(m => ({ default: m.Toaster }))
);

// All pages lazy-loaded for code splitting, reduces initial bundle by ~150KB.
// lazyWithRetry() auto-reloads once on chunk-load failures (stale SPA after
// a redeploy), so visitors don't see "Failed to fetch dynamically imported
// module" when their cached HTML references deleted chunks.
const Home = lazyWithRetry(() => import("./pages/Home"));
const Products = lazyWithRetry(() => import("./pages/Products"));
const ProductDetail = lazyWithRetry(() => import("./pages/ProductDetail"));
const SignIn = lazyWithRetry(() => import("./pages/SignIn"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const VendorRegister = lazyWithRetry(() => import("./pages/VendorRegister"));
const Cart = lazyWithRetry(() => import("./pages/Cart"));
const Checkout = lazyWithRetry(() => import("./pages/Checkout"));
const Vendors = lazyWithRetry(() => import("./pages/Vendors"));
const VendorDetail = lazyWithRetry(() => import("./pages/VendorDetail"));
const VendorDashboard = lazyWithRetry(() => import("./pages/VendorDashboard"));
const VendorProfile = lazyWithRetry(() => import("./pages/VendorProfile"));
const Orders = lazyWithRetry(() => import("./pages/Orders"));
const Notifications = lazyWithRetry(() => import("./pages/Notifications"));
const Categories = lazyWithRetry(() => import("./pages/Categories"));
const CategoryDetail = lazyWithRetry(() => import("./pages/CategoryDetail"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const Wishlist = lazyWithRetry(() => import("./pages/Wishlist"));
const NewPart = lazyWithRetry(() => import("./pages/NewPart"));
const PartsByMake = lazyWithRetry(() => import("./pages/PartsByMake"));
const RequestBoard = lazyWithRetry(() => import("./pages/RequestBoard"));
const ClaimStore = lazyWithRetry(() => import("./pages/ClaimStore"));
const ClaimVendorPage = lazyWithRetry(() => import("./pages/ClaimVendorPage"));
const StaticVendorPage = lazyWithRetry(
  () => import("./pages/StaticVendorPage")
);
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const ResetPassword = lazyWithRetry(() => import("./pages/ResetPassword"));
const QuickList = lazyWithRetry(() => import("./pages/QuickList"));
const TapOutcome = lazyWithRetry(() => import("./pages/TapOutcome"));
// Internal-only design system showcase. Gated server-side via basic-auth;
// see server/_core/index.ts and ARCHITECTURE.md > Design system route.
const DesignSystem = lazyWithRetry(() => import("./pages/DesignSystem"));

/** Client-side fallback for the legacy /auth route, redirects to /sign-in.
 *  The server-side 301 handles direct requests; this catches in-app SPA nav. */
function AuthRedirectToSignIn() {
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate("/sign-in", { replace: true });
  }, [navigate]);
  return null;
}

function ScrollToTop() {
  const [location] = useLocation();
  const isFirst = useRef(true);
  // Tracks whether the most recent navigation came from the browser's
  // back/forward buttons. When it did, we skip the scroll-to-top reset so
  // the browser's native scroll-restoration can put the user back where
  // they were. Without this, a buyer 30 cards into the Products grid who
  // taps a card and hits Back gets dumped at the top of the page — the
  // single highest-friction navigation moment per the 2026-05-22 audit.
  const cameFromPopstate = useRef(false);
  useEffect(() => {
    const onPop = () => {
      cameFromPopstate.current = true;
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  useEffect(() => {
    if (cameFromPopstate.current) {
      // Browser will restore the previous scroll position; do nothing.
      cameFromPopstate.current = false;
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    // Reset focus to <body> so the skip-to-content link is the first
    // tab stop on the new route. Wouter doesn't manage focus by default.
    if (
      document.activeElement instanceof HTMLElement &&
      document.activeElement !== document.body
    ) {
      document.activeElement.blur();
    }
    trackPageView();
  }, [location]);
  return null;
}

/** Initialize Voom tracker and sync auth user ID */
function TrackerInit() {
  const { user } = useAuth();
  useEffect(() => {
    initVoomTracker({
      trackerUrl: "https://voom-ceo-dashboard.replit.app/api/track",
    });
  }, []);
  useEffect(() => {
    if (user?.id) setTrackerUserId(user.id);
  }, [user?.id]);
  return null;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/products" component={Products} />
      <Route path="/products/:id" component={ProductDetail} />
      <Route path="/cart" component={Cart} />
      <Route path="/checkout" component={Checkout} />
      <Route path="/vendors" component={Vendors} />
      <Route path="/vendors/:id" component={VendorDetail} />
      <Route path="/vendor/register" component={VendorRegister} />
      <Route path="/vendor/dashboard" component={VendorDashboard} />
      <Route path="/orders" component={Orders} />
      <Route path="/notifications" component={Notifications} />
      <Route path="/categories/:slug" component={CategoryDetail} />
      <Route path="/categories" component={Categories} />
      <Route path="/admin" component={AdminDashboard} />
      <Route path="/sign-in" component={SignIn} />
      {/* /auth is a legacy duplicate route, server 301-redirects to /sign-in
          in server/_core/index.ts. This client-side fallback handles the
          edge case of in-app navigation to /auth without a server round-trip. */}
      <Route path="/auth" component={AuthRedirectToSignIn} />
      <Route path="/wishlist" component={Wishlist} />
      <Route path="/vendor/profile" component={VendorProfile} />
      <Route path="/vendor/quick-list" component={QuickList} />
      <Route path="/vendor/new-part" component={NewPart} />
      <Route path="/vendor/edit-part/:id" component={NewPart} />
      <Route path="/parts/:make" component={PartsByMake} />
      <Route path="/requests" component={RequestBoard} />
      <Route path="/pricing" component={Pricing} />
      <Route path="/claim/:token" component={ClaimVendorPage} />
      <Route path="/claim" component={ClaimStore} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/v/:slug" component={StaticVendorPage} />
      <Route path="/tap-outcome/:token" component={TapOutcome} />
      <Route path="/design-system" component={DesignSystem} />
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="light">
      <TooltipProvider>
        <Suspense fallback={null}>
          <Toaster />
        </Suspense>
        <WishlistProvider>
          <VehicleProvider>
            <div className="min-h-screen flex flex-col zen-bg">
              <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-md focus:bg-primary focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                Skip to content
              </a>
              <TrackerInit />
              <ScrollToTop />
              <Navbar />
              <main id="main-content" tabIndex={-1} className="flex-1">
                <ErrorBoundary>
                  <Suspense
                    fallback={
                      // CLS FIX (footer travel): the lazy route content is the
                      // *only* thing inside <main>, and <Footer /> renders as a
                      // sibling right below <main>. While a route chunk (esp.
                      // the heavy Home chunk) resolves, this fallback IS the
                      // page height. If it were short (e.g. 60vh), the footer
                      // would paint near/inside the first viewport, then get
                      // shoved down by several screens of content when the
                      // chunk mounted — a large layout shift attributed 100%
                      // to <footer> by PageSpeed (score 0.321).
                      //
                      // Reserving a full viewport (min-h-screen) pushes the
                      // footer below the fold from the very first React paint,
                      // so its later settling to the real content height has
                      // ~zero viewport impact. Works for every route since the
                      // footer is shared; short pages (e.g. 404) just fill the
                      // viewport, which is the expected full-page behaviour.
                      <div className="flex min-h-screen items-center justify-center">
                        <div className="h-8 w-8 rounded-full border-4 border-primary border-t-transparent animate-spin" />
                      </div>
                    }
                  >
                    <Router />
                  </Suspense>
                </ErrorBoundary>
              </main>
              <Footer />
              <MobileBottomNav />
              <FloatingWhatsApp />
              <TapFeedbackListener />
              <FeedbackWidget />
            </div>
          </VehicleProvider>
        </WishlistProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
